import { itemApi } from '@/api/restaurant/itemApi';
import { type Item } from '@/models/restaurant/Item';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { fillHeight } from '../../theme';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import Dialog from '../common/dialog/Dialog';
import FormTextField from '../common/FormTextField';
import { useToast } from '../common/toast/ToastProvider';

const ItemList = () => {
    const app = useAppContext();
    const strings = useLocalizedStrings();
    const toast = useToast();
    const qc = useQueryClient();
    const [search, setSearch] = useState("");
    const [submittedSearch, setSubmittedSearch] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitleName, setDialogTitleName] = useState<string | undefined>();
    const { reset, register, control, handleSubmit: onFormSubmit, formState } = useForm<Item>();

    const locationId = app.locationId ?? undefined;
    const searchText = submittedSearch?.trim() ?? "";
    const isEditing = useMemo(() => dialogTitleName !== undefined, [dialogTitleName]);

    const { data: items, isFetching: isGridLoading } = useQuery<Item[]>({
        queryKey: ['items', locationId, searchText],
        enabled: !!locationId && submittedSearch !== null,
        queryFn: () => itemApi.list(locationId!, searchText || undefined),
        staleTime: 0,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
    const { mutateAsync: saveItem, isPending: isSaving } = useMutation({
        mutationFn: (item: Item) => itemApi.save(locationId!, item),
        onSuccess: () => handleSaveSuccess(),
        onError: () => handleSaveError(),
    });

    const columns: GridColDef<Item>[] = [
        {
            field: 'name',
            headerName: strings.name,
            flex: 2,
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 3,
        },
        {
            field: 'soldOut',
            headerName: 'Sold Out',
            type: 'boolean',
            flex: 1,
        },
        {
            field: 'openPrice',
            headerName: 'Open Price',
            type: 'boolean',
            flex: 1,
        },
        {
            field: 'actions',
            headerName: strings.actions,
            type: 'actions',
            flex: 1,
            getActions: (params: GridRowParams) => [
                <DataGridEditButton id={params.id.toString()} onClick={handleEditClick} aria-label="edit item" />,
            ],
        },
    ];

    useEffect(() => {
        if (!dialogOpen) {
            setDialogTitleName(undefined);
            reset(undefined);
        }
    }, [dialogOpen, reset]);

    const handleSearch = () => {
        setSubmittedSearch(search.trim());
    };

    const handleEditClick = (id: string) => {
        const item = items?.find((i) => i.id === id);
        if (!item) return;

        reset(item);
        setDialogTitleName(item.name);
        setDialogOpen(true);
    };

    const handleSubmit: SubmitHandler<Item> = async (data) => {
        await saveItem(data);
    };

    const handleSaveSuccess = async () => {
        toast({ severity: "success", message: "Item saved." });
        await qc.invalidateQueries({ queryKey: ['items', locationId, searchText] });
        setDialogOpen(false);
    };

    const handleSaveError = () => {
        toast({ severity: "error", message: "Error saving item." });
    };

    return (
        <>
            <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        label="Search items"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSearch();
                            }
                        }}
                        sx={{ width: 360 }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        disabled={!locationId}
                        size="input"
                    >
                        Search
                    </Button>
                </Stack>

                <Box sx={{ mt: 2, flex: 1, minHeight: 0, minWidth: 0 }}>
                    <DataGrid
                        rows={items ?? []}
                        columns={columns}
                        autoPageSize={true}
                        loading={!!locationId && isGridLoading}
                        localeText={{
                            noRowsLabel: !locationId
                                ? 'Select a location'
                                : submittedSearch === null
                                    ? 'Search for items'
                                    : 'No items found',
                        }}
                    />
                </Box>
            </Box>

            <form onSubmit={onFormSubmit(handleSubmit)} id="dialog-item-form">
                <Dialog
                    open={dialogOpen}
                    title={isEditing ? `Edit ${dialogTitleName}` : 'Edit Item'}
                    formId="dialog-item-form"
                    formIsDirty={formState.isDirty}
                    saving={isSaving}
                    saveButtonTitle={strings.save}
                    onCancel={() => setDialogOpen(false)}
                    cancelButtonTitle={strings.cancel}
                >
                    <Stack spacing={2} sx={{ width: 480 }}>
                        <FormTextField
                            id="item-name"
                            label={strings.name}
                            variant="outlined"
                            {...register("name", {
                                required: strings.validationNameRequired,
                            })}
                            isEditing={isEditing}
                            error={!!formState.errors.name}
                            helperText={formState.errors.name?.message || " "}
                        />
                        <FormTextField
                            id="item-description"
                            label="Description"
                            variant="outlined"
                            multiline
                            minRows={3}
                            {...register("description")}
                            isEditing={isEditing}
                        />
                        <Stack direction="row" spacing={2}>
                            <Controller
                                name="soldOut"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                                        label="Sold Out"
                                    />
                                )}
                            />
                            <Controller
                                name="openPrice"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                                        label="Open Price"
                                    />
                                )}
                            />
                        </Stack>
                    </Stack>
                </Dialog>
            </form>
        </>
    );
}

export default ItemList
